// Utility to update sitemap when blogs are created/deleted.
//
// NOTE: this is now a FALLBACK only. The served sitemap is build-owned —
// scripts/generateSitemap.js writes build/sitemap.xml on every deploy, and
// /sitemap.xml no longer proxies to the backend's stored copy. A blog publish
// fires the Amplify rebuild webhook, and that rebuild regenerates the sitemap
// alongside the static blog HTML, so the two can never disagree. This push to
// set-xml keeps the backend copy current for any consumer still reading it.
// Routes are sourced from the same data modules that drive the pages
// (neighborhoods + guides) plus the static core routes, so a blog publish
// re-emits the FULL sitemap — never a truncated one.
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { getBlogModifiedDateIso, isBlogPublished } from './blogPublishDate';
import { neighborhoodList } from '../data/neighborhoods';
import { guideList } from '../data/guides';

export const updateSitemap = async () => {
  try {
    // Fetch all blogs
    const blogsResponse = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/get-blog?site_id=${SITE_CONFIG.SITE_ID}`, {
      headers: { 'X-API-Key': 'treas-api-key-2025' }
    });

    const blogs = blogsResponse.ok ? await blogsResponse.json() : [];
    const publishedBlogs = Array.isArray(blogs) ? blogs.filter(isBlogPublished) : [];
    // Always the apex domain from config — never window.location.origin. This
    // runs in the admin's browser: if they happen to be on www.<domain> when
    // publishing, origin-based URLs would poison the whole sitemap with www
    // while every canonical says apex, and Google stops indexing (this broke
    // premiereteam.com and realtortexoma.com).
    const domain = SEO_CONFIG.DOMAIN.replace(/\/$/, '');
    const today = new Date().toISOString();

    // Full public route set: static core routes + data-driven neighborhoods + guides.
    // Must stay in sync with scripts/routes.js (the generated prerender registry).
    const staticRoutes = [
      { path: '/', changefreq: 'daily', priority: '1.0' },
      { path: '/chat', changefreq: 'monthly', priority: '0.8' },
      { path: '/search', changefreq: 'daily', priority: '0.8' },
      { path: '/blogs', changefreq: 'weekly', priority: '0.8' },
      { path: '/reviews', changefreq: 'weekly', priority: '0.7' },
      { path: '/agent', changefreq: 'monthly', priority: '0.7' },
      { path: '/contact-us', changefreq: 'monthly', priority: '0.8' },
      { path: '/terms-of-service', changefreq: 'yearly', priority: '0.3' },
      { path: '/privacy-policy', changefreq: 'yearly', priority: '0.3' },
      { path: '/api-docs', changefreq: 'monthly', priority: '0.5' }
    ];
    const routes = [
      ...staticRoutes,
      ...neighborhoodList.map(n => ({ path: `/neighborhood/${n.slug}`, changefreq: 'weekly', priority: '0.8' })),
      ...guideList.map(g => ({ path: `/guide/${g.slug}`, changefreq: 'monthly', priority: '0.6' }))
    ];

    // Generate sitemap XML with actual routes
    let sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`;
    routes.forEach(r => {
      sitemap += `
  <url>
    <loc>${domain}${r.path}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${r.changefreq}</changefreq>
    <priority>${r.priority}</priority>
  </url>`;
    });
    sitemap += `
`;

    // Add blog posts
    if (publishedBlogs.length) {
      publishedBlogs.forEach(blog => {
        // Must match scripts/blogSlug.js byte-for-byte — String.trim() ignores
        // its argument, so `.trim('-')` left trailing hyphens the static blog
        // files never had, producing sitemap URLs with no file behind them.
        const slug = blog.title.toLowerCase()
          .replace(/[^a-z0-9 -]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        const lastmod = getBlogModifiedDateIso(blog) || today;
        sitemap += `
  <url>
    <loc>${domain}/blog/${slug}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>
  </url>`;
      });
    }

    sitemap += `
</urlset>`;

    // Send to backend to update sitemap file
    const formData = new FormData();
    const xmlBlob = new Blob([sitemap], { type: 'text/xml' });
    formData.append('xml', xmlBlob, 'sitemap.xml');
    formData.append('site_id', SITE_CONFIG.SITE_ID);

    await fetch('https://tophamtechbe.com/api/treas/ads/v1/set-xml', {
      method: 'POST',
      headers: {
        'X-API-Key': 'treas-api-key-2025'
      },
      body: formData
    });

  } catch (error) {
    console.error('Error updating sitemap:', error);
  }
};
